import React, { useEffect, useState } from "react";
import { Formik, Form } from "formik";
import { useSnack } from "../../services/SnakbarProvider.js";
import RAHeader from "../../molecules/ReceiptAcknowledgements/RAHeader.js";
import RAItems from "../../molecules/ReceiptAcknowledgements/RAItems.js";
import RASpecialItems from "../../molecules/ReceiptAcknowledgements/RASpecialItems.js";
import { Button, Divider, Stack, Typography } from "@mui/material";
import RASummary from "../../molecules/ReceiptAcknowledgements/RASummary.js";
import RAPayments, {
  createEmptyRaPayment,
} from "../../molecules/ReceiptAcknowledgements/RAPayments.js";
import AddExpenseAccountModal from "../../molecules/ReceiptAcknowledgements/AddExpenseAccountModal.js";
import {
  addReceiptAcknowledgement,
  getReceiptAcknowledgementDetail,
  updateReceiptAcknowledgement,
} from "../../api/reciptAcknowledgement/index.js";
import { handleError } from "../../services/errorHandler.js";
import { getProviderDetail } from "../../api/providers/index.js";
import { getPurchaseOrderDetail } from "../../api/purchaseOrders/index.js";
import { getTaxRetentionList } from "../../api/accounting/index.js";
import { getCatalogueByCode } from "../../api/common/catalogues.ts";
import { calculateReceiptAcknowledgementTaxes } from "../../businessLogic/receiptAcknowledgements/taxCalculations.ts";
import dayjs from "dayjs";

const pickCatalogueEntry = (res) => {
  const data = res?.data;
  return Array.isArray(data) ? data[0] : data;
};

const toDateInputValue = (value) => {
  if (!value) return "";
  if (typeof value === "string") return value.substring(0, 10);
  if (value.year && value.month && value.day) {
    return `${value.year}-${String(value.month).padStart(2, "0")}-${String(value.day).padStart(2, "0")}`;
  }
  return "";
};

const mapRaPaymentsFromApi = (receipt) => {
  const payments = receipt?.raPayments;
  if (Array.isArray(payments) && payments.length > 0) {
    return [...payments]
      .sort((a, b) => (a.installmentNo ?? 0) - (b.installmentNo ?? 0))
      .map((payment, index) => ({
        id: payment.id,
        installmentNo: payment.installmentNo ?? index + 1,
        paymentConditionId: payment.paymentConditionId ?? "",
        paymentDate: toDateInputValue(payment.paymentDate),
        amount: payment.amount ?? null,
      }));
  }

  return [
    createEmptyRaPayment(
      1,
      receipt?.total ?? receipt?.paymentTotal ?? null,
      toDateInputValue(receipt?.paymentDate)
    ),
  ];
};

const ReceiptAcknowledgementForm = ({ id, onSubmit, companyId }) => {
  const formMode = id ? "edit" : "insert";
  const [loading, setLoading] = useState(false);
  const { openSnack } = useSnack();
  const [modalOpen, setModalOpen] = useState(false);
  const [currentProviderId, setCurrentProviderId] = useState(null);
  const [refreshExpenseAccounts, setRefreshExpenseAccounts] = useState(0);
  const [taxRetentions, setTaxRetentions] = useState([]);

  const initialValues = {
    selectedProvider: "",
    selectedPurchaseOrder: "",
    selectedRAType: "",
    receiptAcknowledgement: {
      companyId,
      purchaseOrderId: "",
      providerId: "",
      raTypeId: "",
      raType: null,
      invoiceNo: "",
      invoiceDate: dayjs().format("YYYY-MM-DD"),
      paymentDate: "",
      beneficiary: "",
      currencyId: 1, // MXN
      subtotal: 0,
      taxes: 0,
      importe: 0,
      porcentajeDescuento: 0,
      tasaIVA: 16,
      month: new Date().getMonth() + 1,
      year: null,
      // New tax calculation fields
      retainedISR: 0,
      retainedIVA: 0,
      totalIVA: 0,
      totalRetention: 0,
      paymentTotal: 0,
      total: 0,
    },
    items: [
      {
        expenseAccountId: "",
        providerAccountId: "",
        amount: null,
        taxRate: 16,
        percentage: 100,
        expenseAccount: null,
      },
    ],
    raPayments: [createEmptyRaPayment()],
  };

  const [formData, setFormData] = useState(initialValues);



  const fetchData = async () => {
    
    try {
      setLoading(true);
      const res = await getReceiptAcknowledgementDetail(id);
      const [providerData, poRes] = await Promise.all([
        getProviderDetail(res.data.providerId, res.data.companyId),
        res.data.purchaseOrderId
          ? getPurchaseOrderDetail(res.data.purchaseOrderId)
          : Promise.resolve(null),
      ]);
      const payload = {
        receiptAcknowledgement: {
          ...res.data,
          currencyId: res.data.currencyId ?? res.data.currency?.id ?? poRes?.data?.currencyId ?? 1,
        },
        items: res.data.items,
        raPayments: mapRaPaymentsFromApi(res.data),
        selectedProvider: providerData.data,
        selectedPurchaseOrder: poRes?.data || "",
      };

      // For company 1, parse month and year from code (format: YYMM####)
      if (companyId === 1 && res.data?.code?.length >= 4) {
        const yy = parseInt(res.data.code.substring(0, 2), 10);
        const mm = parseInt(res.data.code.substring(2, 4), 10);
        payload.receiptAcknowledgement.year = 2000 + yy;
        payload.receiptAcknowledgement.month = mm;
      }

      setFormData(payload);
    } catch (e) {
      openSnack(handleError(e), "error");
    }
    setLoading(false);
  };

  const createPayload = (values) => {
    const raPayments = (values.raPayments || []).map((payment, index) => ({
      ...(payment.id ? { id: payment.id } : {}),
      installmentNo: index + 1,
      paymentConditionId: payment.paymentConditionId || null,
      paymentDate: payment.paymentDate || null,
      amount: parseFloat(payment.amount) || 0,
    }));
    const firstPaymentDate =
      raPayments.find((payment) => payment.paymentDate)?.paymentDate ||
      values.receiptAcknowledgement.invoiceDate;

    let payload = {
      ...values,
      raPayments,
      receiptAcknowledgement: {
        ...values.receiptAcknowledgement,
        companyId,
        currencyId:
          values.receiptAcknowledgement.currencyId ||
          values.selectedPurchaseOrder?.currencyId ||
          1,
        paymentDate: firstPaymentDate,
      },
    };

    if (values.selectedRAType && values.selectedRAType.code !== "RA_NORMAL" && values.selectedRAType.code !== "RA_SHIPMENT_FEE") {
      payload.items = values.items.map((item) => ({
        ...(item.id ? { id: item.id } : {}),
        expenseAccountId: item.expenseAccountId,
        providerAccountId: values.receiptAcknowledgement.providerAccountId,
        amount: item.amount,
        taxRate: item.taxRate,
      }));
    }
    return payload;
  };

  const validatePayments = (values) => {
    const payments = values.raPayments || [];
    if (payments.length === 0) {
      openSnack("Agrega al menos una parcialidad de pago", "error");
      return false;
    }

    const incomplete = payments.some(
      (payment) => !(parseFloat(payment.amount) > 0) || !payment.paymentDate
    );
    if (incomplete) {
      openSnack("Cada parcialidad necesita monto y fecha de pago", "error");
      return false;
    }

    const total = parseFloat(values.receiptAcknowledgement?.total) || 0;
    const allocated = payments.reduce(
      (sum, payment) => sum + (parseFloat(payment.amount) || 0),
      0
    );
    if (Math.abs(allocated - total) > 0.01) {
      openSnack(
        "La suma de las parcialidades debe igualar el total del contrarrecibo (subtotal + IVA)",
        "error"
      );
      return false;
    }

    return true;
  };

  const handleSubmit = async (values) => {
    if (loading) return; // prevent double submit
    if (!validatePayments(values)) return;
    setLoading(true);
    try {
      const payload = createPayload(values);
      if (formMode == "insert") {
        const submitForm = await addReceiptAcknowledgement(payload);
        if (submitForm.status == 200) {
          openSnack("Contrarrecibo guardado exitosamente", "success");
        }
      } else if (formMode == "edit") {
        const submitForm = await updateReceiptAcknowledgement(id, payload);
        if (submitForm.status == 200) {
          openSnack("Contrarrecibo actualizado exitosamente", "success");
        }
      }
      onSubmit();
    } catch (e) {
      openSnack(handleError(e), "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (formMode == "edit") {
      fetchData();
      return;
    }

    const loadDefaults = async () => {
      try {
        const [paymentMethodRes, raTypeRes] = await Promise.all([
          getCatalogueByCode("PPD"),
          getCatalogueByCode("RA_NORMAL"),
        ]);
        const paymentMethod = pickCatalogueEntry(paymentMethodRes);
        const raType = pickCatalogueEntry(raTypeRes);

        setFormData({
          ...initialValues,
          selectedRAType: raType || "",
          receiptAcknowledgement: {
            ...initialValues.receiptAcknowledgement,
            paymentMethodId: paymentMethod?.id ?? "",
            raTypeId: raType?.id ?? "",
            raType: raType || null,
          },
        });
      } catch (e) {
        console.warn("Error loading RA form defaults:", e);
        setFormData(initialValues);
      }
    };

    loadDefaults();
  }, []);

  useEffect(() => {
    if (!companyId) return;

    const fetchTaxRetentions = async () => {
      try {
        // Match RAService / RARetentionCalculationActuator page size so all company rules are available
        const res = await getTaxRetentionList({
          companyId,
          pageNumber: 1,
          pageSize: 5000,
        });
        setTaxRetentions(Array.isArray(res.data) ? res.data : []);
      } catch (e) {
        console.warn("Error loading tax retention rules:", e);
        setTaxRetentions([]);
      }
    };

    fetchTaxRetentions();
  }, [companyId]);

  return (
    <>
      <Formik
        initialValues={{ ...formData }}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        {({ values, setFieldValue, touched, errors, isSubmitting }) => {
          const isSpecialRA = values.selectedRAType && !["RA_NORMAL", "RA_SHIPMENT_FEE"].includes(values.selectedRAType.code);

          // Calculate subtotal and taxes based on RA type
          let calculatedSubtotal = 0;
          let calculatedTaxes = 0;
          let taxCalculationResult = null;

          if (isSpecialRA) {
            // For special RAs, calculations are handled in RASpecialItems component
            calculatedSubtotal = values.receiptAcknowledgement.subtotal;
            calculatedTaxes = values.receiptAcknowledgement.taxes;
          } else {
            // For normal RAs, calculate from items
            calculatedSubtotal = values.items.reduce(
              (sum, item) => sum + (parseFloat(item.amount) || 0),
              0
            );

            // Retention preview uses TaxRetention catalogue rules (same as RAService.ApplyRetentionRulesAsync)
            try {
              taxCalculationResult = calculateReceiptAcknowledgementTaxes({
                taxRetentions,
                providerTypeId: values.selectedProvider?.legalEntityTypeId ?? null,
                providerTaxRegimeId: values.selectedProvider?.regimeId ?? null,
                items: values.items,
                subtotal: calculatedSubtotal,
              });
              calculatedTaxes = taxCalculationResult.totalIVA;
            } catch (error) {
              console.warn("Error calculating taxes:", error);
              calculatedTaxes = values.items.reduce(
                (sum, item) =>
                  sum + (parseFloat((item.amount * item.taxRate) / 100) || 0),
                0
              );
            }

            // Update Formik's state if values change
            if (values.receiptAcknowledgement.subtotal !== calculatedSubtotal) {
              setFieldValue("receiptAcknowledgement.subtotal", calculatedSubtotal);
            }

            if (values.receiptAcknowledgement.taxes !== calculatedTaxes) {
              setFieldValue("receiptAcknowledgement.taxes", calculatedTaxes);
            }

            // Update tax calculation fields if we have a result
            if (taxCalculationResult) {
              if (values.receiptAcknowledgement.retainedISR !== taxCalculationResult.retainedISR) {
                setFieldValue("receiptAcknowledgement.retainedISR", taxCalculationResult.retainedISR);
              }
              if (values.receiptAcknowledgement.retainedIVA !== taxCalculationResult.retainedIVA) {
                setFieldValue("receiptAcknowledgement.retainedIVA", taxCalculationResult.retainedIVA);
              }
              if (values.receiptAcknowledgement.totalIVA !== taxCalculationResult.totalIVA) {
                setFieldValue("receiptAcknowledgement.totalIVA", taxCalculationResult.totalIVA);
              }
              if (values.receiptAcknowledgement.totalRetention !== taxCalculationResult.totalRetention) {
                setFieldValue("receiptAcknowledgement.totalRetention", taxCalculationResult.totalRetention);
              }
              if (values.receiptAcknowledgement.paymentTotal !== taxCalculationResult.paymentTotal) {
                setFieldValue("receiptAcknowledgement.paymentTotal", taxCalculationResult.paymentTotal);
              }
              if (values.receiptAcknowledgement.total !== taxCalculationResult.total) {
                setFieldValue("receiptAcknowledgement.total", taxCalculationResult.total);
              }
            }
          }

          return (
            <Form>
              <Stack
                direction={"column"}
                spacing={2}
                divider={<Divider orientation="horizontal" flexItem />}
              >
                <Stack direction={"column"}>
                  <Typography variant="h4" mb={2}>
                    Información del contrarrecibo {formMode === "edit" ? values.receiptAcknowledgement.code : ""}
                  </Typography>
                  <RAHeader formMode={formMode}/>
                </Stack>
                <Stack direction={"column"} spacing={2}>
                  <Stack direction={"row"} justifyContent={"space-between"} alignItems={"center"}>
                    <Typography variant="h4">Detalle</Typography>
                    {!isSpecialRA && (
                      <Button
                        variant="outlined"
                        onClick={() => {
                          setCurrentProviderId(values.receiptAcknowledgement.providerId || values.selectedProvider?.id);
                          setModalOpen(true);
                        }}
                        size="small"
                      >
                        Agregar cuenta contable
                      </Button>
                    )}
                  </Stack>
                  {isSpecialRA ? (
                    <RASpecialItems />
                  ) : (
                    <RAItems refreshTrigger={refreshExpenseAccounts} />
                  )}
                </Stack>
                <Stack direction={"column"} spacing={2}>
                  <Typography variant="h4">Parcialidades de pago</Typography>
                  <RAPayments formMode={formMode} />
                </Stack>
                <Stack direction={"column"} spacing={2}>
                  <Typography variant="h4">Resumen</Typography>
                  <RASummary
                    subtotal={values.receiptAcknowledgement.subtotal}
                    taxes={values.receiptAcknowledgement.taxes}
                    retainedISR={values.receiptAcknowledgement.retainedISR}
                    retainedIVA={values.receiptAcknowledgement.retainedIVA}
                    totalIVA={values.receiptAcknowledgement.totalIVA}
                    totalRetention={values.receiptAcknowledgement.totalRetention}
                    paymentTotal={values.receiptAcknowledgement.paymentTotal}
                    total={values.receiptAcknowledgement.total}
                  />
                </Stack>
              </Stack>
              <Button sx={{ mt: 2 }} type="submit" variant="contained" disabled={loading || isSubmitting}>
                {formMode === "edit" ? "Actualizar" : "Añadir"}
              </Button>
            </Form>
          );
        }}
      </Formik>
      
      <AddExpenseAccountModal
        open={modalOpen}
        handleClose={() => setModalOpen(false)}
        companyId={companyId}
        providerId={currentProviderId}
        onSuccess={(newAccount) => {
          // Trigger refresh of expense account dropdowns
          setRefreshExpenseAccounts(prev => prev + 1);
          openSnack(`Cuenta contable "${newAccount.name}" creada exitosamente`, "success");
        }}
      />
    </>
  );
};

export default ReceiptAcknowledgementForm;
